import React from 'react';
import { Box, Container, Typography, styled, Stack } from '@mui/material';
import { motion } from 'framer-motion';

// --- STYLED COMPONENTS ---

const SectionWrapper = styled(Box)({
    padding: '120px 0',
    backgroundColor: '#000',
    color: '#fff',
    position: 'relative',
    overflow: 'hidden',
});

const FlexContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: '60px',
    [theme.breakpoints.down('md')]: { flexDirection: 'column' }
}));

const StarCard = styled(motion.div)({
    position: 'relative',
    borderRadius: '20px',
    background: 'rgba(255, 255, 255, 0.02)',
    border: '1px solid rgba(255, 255, 255, 0.08)',
    padding: '35px',
    marginBottom: '25px',
    overflow: 'hidden',
    zIndex: 1,
});

// Star Streak - Refined for a slower, cinematic look
const StarStreak = styled(motion.span)(({ vertical }) => ({
    position: 'absolute',
    display: 'block',
    background: vertical 
        ? 'linear-gradient(180deg, transparent, #3b82f6, #fff, transparent)' 
        : 'linear-gradient(90deg, transparent, #3b82f6, #fff, transparent)',
    zIndex: 2,
    // Horizontal stars are wide/thin, Vertical stars are tall/thin
    width: vertical ? '2px' : '120px',
    height: vertical ? '120px' : '2px',
}));

// --- COMPONENT ---

const DetailedIntro = () => {
    const data = [
        { title: "Technical Architecture", content: "Specializing in Android & Flutter, I build robust mobile ecosystems. With experience at Technogigz and RudderSoft, I focus on scalable architectures using Kotlin, Jetpack Compose, and modern design patterns." },
        { title: "Industrial Expertise", content: "Beyond standard apps, I work with RFID, NFC, and Zebra handhelds. I specialize in bridging hardware capabilities with smooth software interfaces, from inventory systems to AI-powered tools." },
        { title: "Leadership & Quality", content: "I value clear communication and team growth. Having managed technical hirings, I ensure that every project I touch meets the highest industry standards for performance and maintainability." }
    ];

    // Animation settings for a slower star
    const starTransition = {
        duration: 2.5, // Yahan se speed dheemi ki hai (Higher = Slower)
        repeat: Infinity,
        ease: "linear",
    };

    return (
        <SectionWrapper>
            <Box sx={{ 
                position: 'absolute', inset: 0, 
                backgroundImage: 'radial-gradient(circle at 30% 50%, rgba(59, 130, 246, 0.07) 0%, transparent 60%)',
                zIndex: 0 
            }} />

            <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 1 }}>
                <FlexContainer>
                    
                    {/* LEFT (40%) */}
                    <Box sx={{ 
                        flex: '0 0 40%', 
                        position: 'sticky', 
                        top: '100px' }}>

                        <Typography 
                        variant="overline" 
                        sx={{ 
                            color: '#3b82f6', 
                            fontWeight: 900, 
                            letterSpacing: 4 
                            }}>
                                PHILOSOPHY
                        </Typography>
                        
                        <Typography variant="h3" sx={{ fontWeight: 900, mt: 2, mb: 3, lineHeight: 1.1 }}>
                            Precision in Every <br /> <span style={{ color: '#3b82f6' }}>Line of Code.</span>
                        </Typography>
                        <Typography sx={{ color: '#64748b', fontSize: '1.1rem', lineHeight: 1.8, maxWidth: '400px' }}>
                            My approach to development is rooted in the belief that 
                            <b style={{ color: '#fff' }}> Exceptional Software </b> 
                            is a symphony of hidden logic and visible elegance. 
                            <br /><br />I don't just write code; I architect
                            <span style={{ color: '#3b82f6' }}> scalable ecosystems </span> 
                            that are as clean and maintainable on the inside as they are
                             intuitive and immersive on the outside. For me,<b style={{ color: '#fff' }}> Performance </b>
                             is not a feature—it's a foundation.
                        </Typography>
                    </Box>

                    {/* RIGHT (60%) */}
                    <Box sx={{ flex: '0 0 60%' }}>
                        <Stack>
                            {data.map((item, index) => (
                                <StarCard
                                    key={index}
                                    whileHover="hover"
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.5 }}
                                >
                                    {/* Top Side Star */}
                                    <StarStreak 
                                        style={{ top: 0, left: '-100%' }} 
                                        variants={{ hover: { left: '100%', transition: starTransition } }} 
                                    />
                                    {/* Bottom Side Star */}
                                    <StarStreak 
                                        style={{ bottom: 0, right: '-100%' }} 
                                        variants={{ hover: { right: '100%', transition: { ...starTransition, delay: 1.2 } } }} 
                                    />
                                    {/* Right Side Star (Vertical) */}
                                    <StarStreak 
                                        vertical
                                        style={{ right: 0, top: '-100%' }} 
                                        variants={{ hover: { top: '100%', transition: { ...starTransition, delay: 0.6 } } }} 
                                    />
                                    {/* Left Side Star (Vertical) */}
                                    <StarStreak 
                                        vertical
                                        style={{ left: 0, bottom: '-100%' }} 
                                        variants={{ hover: { bottom: '100%', transition: { ...starTransition, delay: 1.8 } } }} 
                                    />

                                    <Typography variant="h6" sx={{ color: '#3b82f6', fontWeight: 800, mb: 1.5 }}>{item.title}</Typography>
                                    <Typography sx={{ color: '#94a3b8', fontSize: '1.05rem', lineHeight: 1.8 }}>{item.content}</Typography>
                                </StarCard>
                            ))}
                        </Stack>
                    </Box>
                </FlexContainer>
            </Container>
        </SectionWrapper>
    );
};

export default DetailedIntro;
